import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Divider, Grid } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import SendIcon from '@material-ui/icons/Send';
import { useTranslation } from 'react-i18next';
import TextInput from '../../../form/components/TextInput';
import Form from '../../../form/components/Form';
import Select from '../../../form/components/Select';
import Editor from '../../../form/components/Editor';
import MultiChipSelect from '../../../form/components/MultiChipSelect';
import { useEmailTemplate } from '../../../mailTemplates/emailTemplateSlice';
import {
    selectAllEmailReceiverGroups,
    useEmailReceiverGroups,
} from '../../emailReceiverGroupSlice';
import FileTreeAttachments from '../../../files/components/FileTree/FileTreeAttachments';
import { useContactsByCustomerId } from '../../../contacts/contactsSlice';
import {
    CONTRACTING_PARTNER_CONTACT,
    useContactsByCustomerIdWithType,
} from '../../../contacts/hooks';
import { useContractsByCustomerId } from '../../../contracts/contractsSlice';
import { selectCustomerById, updateCustomer } from '../../../customers/customersSlice';
import { EMAIL_RECEIVER_GROUPS } from '../../../mailTemplates/components/MailTemplateForm';
import SubmitButton from '../../../form/components/SubmitButton';
import { useApi } from '../../../api/components/ApiProvider';
import { EMAIL_RESOURCE } from '../../../api/resources';
import { selectAllAgencies } from '../../../agencies/agenciesSlice';
import { selectAllLocations, useLocations } from '../../../locations/locationsSlice';
import { replaceEditorTags } from '../../../mailTemplates/utils';
import { IdPropType, RefPropType } from '../../../../proptypes/basic';
import { MailAttachmentPropType, PrefillMailPropType } from '../../proptypes';
import { sendMailSchema } from '../../schema';
import { selectAllEmployees, selectEmployeeById } from '../../../employees/employeesSlice';
import { asAbsolutePath } from '../../../files/utils';
import { useUser } from '../../../auth/hooks';
import {
    selectAllEmailInboxes,
    useEmailInboxes,
    useEmailInboxAddressBySlug,
} from '../../../emailInboxes/emailInboxesSlice';
import {
    EMAIL_INBOX_SLUG_BUCHHALTUNG,
    EMAIL_INBOX_SLUG_FRANCHISE_SUPPORT,
} from '../../../emailInboxes/constants';
import { useSnackbar } from '../../../snackbar/hooks';
import { selectLocationById } from '../../../locations/locationsSlice';
import {
    NURSE_REQUEST_ENTITY,
    NURSE_REQUEST_CANCELATION_ENTITY,
    INFOMATERIAL_ENTITY,
} from '../../../mailTemplates/entities';
import {
        createOrUpdateEmailDraft, 
        loadEmailDraft, 
} from 'Modules/mails/utils';
import Alert from '@material-ui/lab/Alert';

const useStyles = makeStyles((theme) => ({
    button: {
        marginBottom: theme.spacing(2),
    },

    end: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    dividerMargin: {
        marginBottom: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
}));

const getCloudRoots = (customer) =>
    customer ? ['/PzH/Anhänge', asAbsolutePath(customer.cloudFolder)] : ['/PzH/Anhänge'];

const SendMailForm = ({
    onSent,
    entity,
    entityId,
    templateId,
    submitRef,
    attachments,
    extraEntities,
    prefill,
    draft,
    draftId,
    draftSaveRef,
    onCancel,
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const api = useApi();
    const { enqueueSnackbar } = useSnackbar();

    const dispatch = useDispatch();
    const [draftData, setDraftData] = useState(undefined); 
    const draftSaveQueue = useRef(Promise.resolve());
    const createdDraftIdRef = useRef(null);
    const initialDraftSyncIdRef = useRef(null);
    const customerId = entityId;

    const saveDraft = useCallback((data) => {
        const request = draftSaveQueue.current
            .catch(() => undefined)
            .then(() => createOrUpdateEmailDraft({ data }));
        draftSaveQueue.current = request;
        return request;
    }, []);

    if (draftSaveRef) {
        draftSaveRef.current = () => draftSaveQueue.current;
    }

    const [contacts] = useContactsByCustomerId(customerId);

    const [contractingPartners] = useContactsByCustomerIdWithType(
        customerId,
        CONTRACTING_PARTNER_CONTACT
    );

    useLocations();
    const [contracts] = useContractsByCustomerId(customerId);
    useEmailReceiverGroups();

    const [emailTemplate] = useEmailTemplate(templateId);

    const currentUser = useUser();
    const currentEmployee = useSelector((state) =>
        selectEmployeeById(state, currentUser.employeeId)
    );

    const currentLocation = useSelector((state) =>
        selectLocationById(state, currentEmployee.locationId)
    );
    const isCurrentLocationPremium = currentLocation ? currentLocation.isPremium : false;
    const isHamburg = currentLocation
        ? currentLocation.id === parseInt(process.env.REACT_APP_LOCATION_HAMBURG_ID, 10)
        : false
   
  useEffect(() => {
        // An existing draft can temporarily disappear from the Redux cache
        // after it is sent, just before this dialog unmounts. Its draftId is
        // still present during that render, so do not interpret it as a new
        // email and create an empty replacement draft.
        if (draftId && !draft) {
            return;
        }

        // if it's a template based email and no draft yet, create a new draft
        if (emailTemplate && !draft) {
            console.log('SendMailForm: creating new draft for templateId ', templateId, emailTemplate);
            const date = new Date().toISOString();
            const data = {
                owner_employee_id: currentUser.employeeId,
                customer_id: entityId || null,
                parent_id: null,
                created_at: date,
                updated_at: date,
                customerId: entityId || null,
                email_template_id: templateId,
                subject: emailTemplate.subject || '',
                body: emailTemplate.text || '',
                entity_name: entity || null,
                extra_fields:
                    extraEntities?.careNeedId != null
                        ? { careNeedId: extraEntities.careNeedId }
                        : null,
            };
            createOrUpdateEmailDraft({data}).then((response) => {
                console.log('SendMailForm: created new draft =>', response);
                createdDraftIdRef.current = response.id;

                // update local draftData id
                const update = {
                    ...data,
                    id: response.id,
                };
                setDraftData(update);

            }).catch((error) => {
                console.error('SendMailForm: error creating new draft =>', error);
            });
        }else if (draft) {
            //console.log('SendMailForm: draft found =>', draft);
            // there is a draft, load its full data
            const d = draft;
            if (d == null) {
                console.warn('SendMailForm: draft is null');
            } else {

                // load the full draft data including receivers
                loadEmailDraft(d.id).then((response) => {
                    //console.log('SendMailForm: loaded full draft data =>', response);
                    setDraftData(response);
                }).catch((error) => {
                    console.error('SendMailForm: error loading full draft data =>', error);
                });
            }
        }else {
            // maybe its a new email without template , so create an empty draft with default values
            console.log('SendMailForm: no draft and no template, setting draftData to null');

            const data = {
                owner_employee_id: currentUser.employeeId,
                customer_id: entityId || null,
                parent_id: null,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
                customerId: entityId || null,
                email_template_id: null,
                entity_name: entity || null,
                extra_fields:
                    extraEntities?.careNeedId != null
                        ? { careNeedId: extraEntities.careNeedId }
                        : null,
            };
            createOrUpdateEmailDraft({data}).then((response) => {
                console.log('SendMailForm: created new empty draft =>', response);
                createdDraftIdRef.current = response.id;
                // update local draftData id
                const update = {
                    ...data,
                    id: response.id,
                };
                setDraftData(update);

            }).catch((error) => {
                console.error('SendMailForm: error creating new empty draft =>', error);
                setDraftData(null);
            });
        }

    }, [draft, draftId, emailTemplate, templateId, currentUser.employeeId, entityId]);

    // console.log("user",currentUser);
    // console.log("currentEmployee",currentEmployee);
    //console.log("location",currentLocation, "isCurrentLocationPremium",isCurrentLocationPremium);

    useEmailInboxes();
    const emailInboxes = useSelector(selectAllEmailInboxes);
    const fromAddressOptionsNotEditable = useMemo(() => {
        return emailInboxes
            .filter((emailInbox) => emailTemplate && emailInbox.id === emailTemplate.emailInboxId && emailInbox.isActive === true)
            .map((emailInbox) => ({
                label: emailInbox.name,
                value: emailInbox.name,
            }));
    }, [emailInboxes, emailTemplate]);

    // look for only premium email accounts as 'from' option
    //const fromAddressOptionsPremium = useMemo(() => {
    //    return emailInboxes
    //        .filter((emailInbox) => emailInbox.isPremium === true && emailInbox.isActive === true)
    //        .map((emailInbox) => ({
    //            label: emailInbox.name,
    //            value: emailInbox.name,
    //        }));
    //}, [emailInboxes]);

    //console.log("emailInboxDefaultEditable=>",emailTemplate?.emailInboxDefaultEditable, fromAddressOptionsNotEditable);
    //console.log("emailInboxes=>",currentEmployee.emailInboxes,"fromAddressOptionsPremium=>",fromAddressOptionsPremium);

    const locations = useSelector(selectAllLocations);
    const employees = useSelector(selectAllEmployees);

    const agencies = useSelector(selectAllAgencies);
    const customer = useSelector((state) => selectCustomerById(state, customerId));
    const receiverGroups = useSelector(selectAllEmailReceiverGroups);

    const isCurrentCustomerPremium = useMemo(() => {
        return (customer?.isPremium === true || customer?.isPremium === 1) ? true : false;
    }, [customer]);
    //console.log('isCurrentCustomerPremium', isCurrentCustomerPremium);

    const roots = getCloudRoots(customer);

    const defaultEmailId = useMemo(() => {
        return currentEmployee.emailInboxes
            .filter((e) => e.isPremium === false  && e.isActive === true)
            .map((e) => e.isDefault)
            .indexOf(1);
    }, [currentEmployee]);

    const fromAddresses = useMemo(() => {
        // console.log(
        //     'fromAddresses',
        //     currentEmployee?.emailInboxes.map((ibx) => ibx.isPremium), "isHamburg",
        //     isHamburg,
        //     "isCurrentCustomerPremium",
        //     isCurrentCustomerPremium
        // );

        if (isHamburg === true && isCurrentCustomerPremium === true) {
            return currentEmployee.emailInboxes
                .filter((e) => e.isPremium === isCurrentCustomerPremium && e.isActive === true)
                .map((ibx) => ibx.name);
        }
        return currentEmployee.emailInboxes.filter((e) => e.isActive === true).map((ibx) => ibx.name);
    }, [currentEmployee, isCurrentCustomerPremium, isHamburg]);
   //console.log("fromAddresses",fromAddresses);


    const fromAddressOptions = useMemo(() => {
        return fromAddresses.map((addr) => ({
            label: addr,
            value: addr,
        }));
    }, [fromAddresses]);

    const activeFromAddress = useMemo(() => {
        if (isHamburg && isCurrentCustomerPremium && isCurrentLocationPremium) {
            return fromAddressOptions[0]?.value;
        } else {

            //console.log("defaultEmailId",defaultEmailId, "fromAddressOptions",fromAddressOptions, "emailTemplate",emailTemplate);

            if (emailTemplate && emailTemplate.emailInboxIsLoggedInEmployeeStdInbox) {
                return currentEmployee.emailInboxes.find((e) => e.isDefault === 1)?.name;
            }

            return emailTemplate && !emailTemplate.emailInboxDefaultEditable
                ? fromAddressOptionsNotEditable[0]?.value
                : fromAddressOptions[defaultEmailId]?.value;
        }
    }, [
        fromAddressOptions,
        defaultEmailId,
        emailTemplate,
        fromAddressOptionsNotEditable,
        isCurrentCustomerPremium,
        isCurrentLocationPremium,
        isHamburg,
        currentEmployee,
    ]);

    const allAttachments = useMemo(() => {
        const result = [];

        if (attachments) {
            attachments.forEach((item) => result.push(item));
        }

        if (emailTemplate && emailTemplate.attachments) {
            emailTemplate.attachments
                .filter((att) => att.type === 'static')
                .forEach((att) =>
                    result.push({
                        path: att.path,
                        locked: !emailTemplate.attachmentsStaticEditable,
                    })
                );
        }

        return result;
    }, [attachments, emailTemplate]);

    // Resolves the address of the manually-added "Buchhaltung" / "Franchise"
    // receiver entries by slug from the email inboxes table instead of a
    // hardcoded address, so it can differ between the testing server and the
    // live system. Both inboxes are currently marked inactive, and the
    // regular emailInboxes list (selectAllEmailInboxes/useEmailInboxes,
    // used elsewhere in this component) only ever contains active inboxes
    // (see #848), so it can never resolve them - useEmailInboxAddressBySlug
    // fetches by slug directly instead. Falls back to the previously
    // hardcoded address while loading or if no inbox with that slug exists,
    // so a wrong/missing slug never breaks sending.
    const buchhaltungEmailAddress = useEmailInboxAddressBySlug(EMAIL_INBOX_SLUG_BUCHHALTUNG);
    const franchiseSupportEmailAddress = useEmailInboxAddressBySlug(EMAIL_INBOX_SLUG_FRANCHISE_SUPPORT);

    const receiverOptions = useMemo(() => {
        const result = [];

        const iterateGroups = emailTemplate
            ? emailTemplate.receiverGroups
            : receiverGroups.map((g) => g.id);

        iterateGroups.forEach((id) => {
            const group = receiverGroups.find((g) => g.id === id);

            if (!group) {
                return;
            }

            switch (group.name) {
                case 'customer.display_contact':
                    contacts
                        .filter((contact) => {
                            return contact.isPrimaryContact;
                        })
                        .filter((contact) => {
                            return !!contact.email;
                        })
                        .map((contact) => {
                            return {
                                name: group.name,
                                groupTitle: EMAIL_RECEIVER_GROUPS[group.name] || '',
                                label: `${contact.firstname || ''} ${contact.lastname} <${
                                    contact.email
                                }>`,
                                value: `contact_${contact.id}`,
                                contact,
                            };
                        })
                        .forEach((i) => result.push(i));

                    break;
                case 'customer.contract_contact':
                    contacts
                        .filter((contact) => {
                            return contact.isContractingPartner;
                        })
                        .filter((contact) => {
                            return !!contact.email;
                        })
                        .map((contact) => {
                            return {
                                name: group.name,
                                groupTitle: EMAIL_RECEIVER_GROUPS[group.name] || '',
                                label: `${contact.firstname || ''} ${contact.lastname} <${
                                    contact.email
                                }>`,
                                value: `contact_${contact.id}`,
                                contact,
                            };
                        })
                        .forEach((i) => result.push(i));

                    break;
                case 'customer.nurse_suggestions_contact':
                    contacts
                        .filter((contact) => {
                            return contact.sendNurseSuggestions;
                        })
                        .filter((contact) => {
                            return !!contact.email;
                        })
                        .map((contact) => {
                            return {
                                name: group.name,
                                groupTitle: EMAIL_RECEIVER_GROUPS[group.name] || '',
                                label: `${contact.firstname || ''} ${contact.lastname} <${
                                    contact.email
                                }>`,
                                value: `contact_${contact.id}`,
                                contact,
                            };
                        })
                        .forEach((i) => result.push(i));

                    break;
                case 'internal_accounts':
                    emailInboxes
                        // show only the active email inboxes
                        .filter((emailInbox) => emailInbox.isActive === true)
                        // BAD IDEA: filter zzz to prevent it showing up as receiver
                        .filter((emailInbox) => {
                            const label = emailInbox.slug || emailInbox.name;
                            return !label.toLowerCase().includes('zzz');
                        })
                        // BAD IDEA: filter buchhaltung to prevent it showing up as receiver
                        .filter((emailInbox) => {
                            const label = emailInbox.slug || emailInbox.name;
                            return !label.toLowerCase().includes('buchhaltung');
                        })
                        .map((emailInbox) => {
                            const label = emailInbox.slug || emailInbox.name;
                            const arr = label.split(' ');
                            for (var i = 0; i < arr.length; i++) {
                                arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1);
                            }
                            //Join all the elements of the array back into a string
                            //using a blankspace as a separator
                            const label2 = arr.join(' ');
                            return {
                                name: group.name,
                                groupTitle: EMAIL_RECEIVER_GROUPS[group.name] || '',
                                label: label2,
                                value: `internal_${emailInbox.id}`,
                            };
                        })
                        .forEach((i) => result.push(i));
                    break;
                case 'agencies':
                    agencies
                        .filter((agency) => {
                            const label = agency.matchCode || agency.name;
                            return !label.toLowerCase().includes('zzz');
                        })
                        .map((agency) => {
                            return {
                                name: group.name,
                                groupTitle: EMAIL_RECEIVER_GROUPS[group.name] || '',
                                label: agency.matchCode || agency.name,
                                value: `agency_${agency.id}`,
                            };
                        })
                        .forEach((i) => result.push(i));
                    break;
                case 'locations':
                    locations
                        .map((location) => {
                            return {
                                name: group.name,
                                groupTitle: EMAIL_RECEIVER_GROUPS[group.name] || '',
                                label: location.name,
                                value: `location_${location.id}`,
                            };
                        })
                        .forEach((i) => result.push(i));
                    break;
                case 'customer.nurse_request.agencies':
                    agencies
                        .filter((a) => extraEntities?.agencyIds?.includes(a.id, 0))
                        .filter((agency) => {
                            const label = agency.matchCode || agency.name;
                            return !label.toLowerCase().includes('zzz');
                        })
                        .map((agency) => {
                            return {
                                name: group.name,
                                groupTitle: EMAIL_RECEIVER_GROUPS[group.name] || '',
                                label: agency.matchCode || agency.name,
                                value: `agency_${agency.id}`,
                            };
                        })
                        .forEach((i) => result.push(i));
                    break;
                case 'customer.location': {
                    const location = customer
                        ? locations.find(({ id }) => id === customer.locationId)
                        : undefined;
                    if (location) {
                        [
                            {
                                name: group.name,
                                groupTitle: EMAIL_RECEIVER_GROUPS[group.name] || '',
                                label: `${location.name} <${location.matchCode}>`,
                                value: `location_${location.id}`,
                            },
                        ].forEach((i) => result.push(i));
                    }
                    break;
                }
                case 'customer.contacts':
                    contacts
                        .filter((contact) => {
                            return !!contact.email;
                        })
                        .map((contact) => {
                            return {
                                name: group.name,
                                groupTitle: EMAIL_RECEIVER_GROUPS[group.name] || '',
                                label: `${contact.firstname || ''} ${contact.lastname} <${
                                    contact.email
                                }>`,
                                value: `contact_${contact.id}`,
                                contact,
                            };
                        })
                        .forEach((i) => result.push(i));
                    break;
                case 'customer.agencies':
                    contracts
                        .reduce((carry, contract) => {
                            if (contract.agencyId && !carry.includes(contract.agencyId)) {
                                carry.push(contract.agencyId);
                            }
                            return carry;
                        }, [])
                        .map((agencyId) => agencies.find((agency) => agency.id === agencyId))
                        .filter((agency) => !!agency)
                        .map((agency) => {
                            return {
                                name: group.name,
                                groupTitle: EMAIL_RECEIVER_GROUPS[group.name] || '',
                                label: agency.matchCode || agency.name,
                                value: `agency_${agency.id}`,
                            };
                        })
                        .forEach((i) => result.push(i));
                    break;
                case 'manual_addresses':
                    // add some manual addresses
                    result.push({
                        name: group.name,
                        groupTitle: 'Manuelle Adressen',
                        label: `Buchhaltung <${
                            buchhaltungEmailAddress || 'buchhaltung@pflegezuhause.info'
                        }>`,
                        value: `manualcontacts_buchhaltung`,
                    });
                    result.push({
                        name: group.name,
                        groupTitle: 'Manuelle Adressen',
                        label: `Franchise <${
                            franchiseSupportEmailAddress || 'franchise-support@pflegezuhause.info'
                        }>`,
                        value: `manualcontacts_franchise`,
                    });
                    break;
                default:
                    break;
            }
        });

        //console.log("receiverOptions =>", result);

        return result;
    }, [
        emailTemplate,
        emailInboxes,
        receiverGroups,
        contacts,
        customer,
        contracts,
        agencies,
        locations,
        extraEntities,
        buchhaltungEmailAddress,
        franchiseSupportEmailAddress,
    ]);

    function defaultEntity(emailTemplate, receiverGroups, receiverOptions, field) {
        if (!emailTemplate) {
            return [];
        }

        const group = receiverGroups.find((g) => String(g.id) === String(emailTemplate[field]));
        if (!group) {
            return [];
        }

        return receiverOptions.filter((g) => g.name === group.name);
    }

    const receiverDefault = useMemo(() => {
        const def = defaultEntity(
            emailTemplate,
            receiverGroups,
            receiverOptions,
            'receiverDefault'
        );

        return def.map((a) => a.value);
    }, [emailTemplate, receiverGroups, receiverOptions]);

    const isSetUseReceiverSeperately =
        emailTemplate?.entityName === NURSE_REQUEST_CANCELATION_ENTITY ||
        emailTemplate?.entityName === NURSE_REQUEST_ENTITY;
    // TODO duplicate code
    const ccDefault = useMemo(() => {
        if (!emailTemplate) {
            return [];
        }

        const group = receiverGroups.find((g) => String(g.id) === String(emailTemplate.ccDefault));
        if (!group) {
            return [];
        }

        const optionGroup = receiverOptions.find((g) => g.name === group.name);
        return optionGroup ? [optionGroup.value] : [];
        // FIXME: is this needed?
        // if (!optionGroup) {
        //     return [];
        // }
        //
        // return optionGroup ? [optionGroup.options[0].value] : [];
    }, [emailTemplate, receiverGroups, receiverOptions]);

    const onRefresh = useCallback(
        (setValue, selectedReceiverValue) => {
            const def = defaultEntity(
                emailTemplate,
                receiverGroups,
                receiverOptions,
                'receiverDefault'
            );

            let selectedContact = null;
            if (selectedReceiverValue && selectedReceiverValue[0]) {
                const firstSelected = selectedReceiverValue[0];

                if (firstSelected.indexOf('contact_') === 0) {
                    const selectedId = parseInt(firstSelected.replace('contact_', ''), 10);
                    selectedContact = contacts.find(({ id }) => id === selectedId);
                }
            }

            const contact = selectedContact || def[0]?.contact;

            const { text } = replaceEditorTags(
                emailTemplate?.subject,
                emailTemplate?.text,
                customer,
                currentUser,
                contact,
                contacts,
                locations,
                employees,
                extraEntities,
                contractingPartners,
                t
            );

            setValue(text);
        },
        [
            emailTemplate,
            customer,
            contacts,
            currentUser,
            extraEntities,
            receiverGroups,
            receiverOptions,
            t,
            employees,
            locations,
            contractingPartners,
        ]
    );

    const normalizeDraftReceiverValue = useCallback((item) => {
        if (!item) {
            return '';
        }

        if (typeof item !== 'object') {
            return String(item);
        }

        if (item.value) {
            return item.value;
        }

        const emailAddress = item.emailAddress || item.email_address;
        if (emailAddress?.address) {
            return emailAddress.name
                ? `${emailAddress.name} <${emailAddress.address}>`
                : emailAddress.address;
        }

        return item.email || item.address || item.name || '';
    }, []);

    const normalizeDraftFieldValue = useCallback(
        (fieldName, value) => {
            if (!['to', 'cc', 'bcc', 'from'].includes(fieldName)) {
                return value;
            }

            if (!Array.isArray(value)) {
                return normalizeDraftReceiverValue(value);
            }

            return value.map(normalizeDraftReceiverValue).filter((item) => !!item);
        },
        [normalizeDraftReceiverValue]
    );

    // maps incoming database items (strings or structures) back into UI Option values
    const reverseResolveReceivers = useCallback(
        (rawArray) => {
            if (!rawArray || !Array.isArray(rawArray)) return undefined;

            return rawArray
                .map((item) => {
                    const rawString = normalizeDraftReceiverValue(item);
                    const cleanEmail = rawString
                        .replace(/.*</, '')
                        .replace(/>.*/, '')
                        .trim()
                        .toLowerCase();

                    const foundOption = receiverOptions.find((opt) => {
                        const optClean = opt.label
                            .replace(/.*</, '')
                            .replace(/>.*/, '')
                            .trim()
                            .toLowerCase();
                        return optClean === cleanEmail || opt.value.toLowerCase() === cleanEmail;
                    });

                    return foundOption ? foundOption.value : rawString;
                })
                .filter((item) => !!item);
        },
        [normalizeDraftReceiverValue, receiverOptions]
    );

    const initialValues = useMemo(() => {
        const def = defaultEntity(emailTemplate, receiverGroups, receiverOptions, 'receiverDefault');
        const contact = def?.length > 0 ? def[0]?.contact : undefined;

        // Determine source strings for tag replacement
        const sourceSubject = draftData?.subject || draftData?.body_title || emailTemplate?.subject || prefill?.subject || '';
        const sourceBody = draftData?.body || draftData?.text || emailTemplate?.text || prefill?.text || '';

        const { subject: processedSubject, text: processedBody } = replaceEditorTags(
            sourceSubject,
            sourceBody,
            customer,
            currentUser,
            contact,
            contacts,
            locations,
            employees,
            extraEntities,
            contractingPartners,
            t
        );

        return {
            from: draftData?.from || activeFromAddress,
            receiver: reverseResolveReceivers(draftData?.emailReceivers || draftData?.to) || prefill?.receivers || receiverDefault || [],
            cc: reverseResolveReceivers(draftData?.emailReceiversCc || draftData?.cc) || prefill?.cc || ccDefault || [],
            bcc: reverseResolveReceivers(draftData?.emailReceiversBcc || draftData?.bcc) || [],
            attachments: draftData?.attachments || prefill?.attachments || allAttachments,
            subject: processedSubject || '',
            text: processedBody || '',
            parentId: draftData?.parentId || prefill?.parentId || null,
            customerId: draftData?.customerId || prefill?.customerId || entityId || null,
            email_template_id: draftData?.emailTemplateId || prefill?.emailTemplateId || templateId || null,
        };
    }, [
        emailTemplate,
        customer,
        contacts,
        receiverDefault,
        ccDefault,
        allAttachments,
        currentUser,
        extraEntities,
        receiverGroups,
        receiverOptions,
        prefill,
        t,
        employees,
        entityId,
        locations,
        contractingPartners,
        activeFromAddress,
        reverseResolveReceivers,
        draftData,
    ]);

    // Sync only drafts created by this form. Existing drafts load their saved
    // receivers asynchronously; syncing during that race can erase recipients.
    useEffect(() => {
        const isCreatedInThisForm =
            draftData && draftData.id && createdDraftIdRef.current === draftData.id;
        const hasReceiverDefault =
            emailTemplate && emailTemplate.receiverDefault && initialValues.receiver.length === 0;

        if (
            isCreatedInThisForm &&
            initialDraftSyncIdRef.current !== draftData.id &&
            !hasReceiverDefault
        ) {
            const data = {
                ...initialValues,
                id: draftData.id,
                to: initialValues.receiver,
                body: initialValues.text,
                customer_id: initialValues.customerId,
                parent_id: initialValues.parentId,
                owner_employee_id: currentUser.employeeId,
                email_template_id: templateId,
            };
            initialDraftSyncIdRef.current = draftData.id;
            saveDraft(data).catch((error) => {
                initialDraftSyncIdRef.current = null;
                console.error('SendMailForm: error syncing draft =>', error);
            });
        }
    }, [
        draftData,
        emailTemplate,
        initialValues,
        currentUser.employeeId,
        saveDraft,
        templateId,
    ]);

    const handleSubmit = (values) => {
        const resolveEmailAddresses = (receiver) => {
            if (receiver.indexOf('@') !== -1) {
                const regex1 = /<(.*)>/g;
                let matches = regex1.exec(receiver);
                if (matches !== null && matches.length > 1) return matches[1];
                const regex2 = /\((.*)\)/g;
                matches = regex2.exec(receiver);
                if (matches !== null && matches.length > 1) return matches[1];

                let cut = receiver.replace(/.*</, '');
                cut = receiver.replace(/>.*/, '');
                return cut;
            }

            if (!receiver.split) {
                return 'null-mail';
            }

            const [eentity, eentityId] = receiver.split('_', 2);
            switch (eentity) {
                case 'manualcontacts':
                    switch (eentityId) {
                        case 'buchhaltung':
                            return buchhaltungEmailAddress || 'buchhaltung@pflegezuhause.info';
                        case 'franchise':
                            return (
                                franchiseSupportEmailAddress || 'franchise-support@pflegezuhause.info'
                            );
                        default:
                            return receiver;
                    }
                case 'contact':
                    return contacts.find(({ id }) => id === parseInt(eentityId, 10))?.email;
                case 'agency': {
                    const defaultAgencyEmployee = employees.find((e) => {
                        return String(e.agencyId) === String(eentityId) && e.isPrimaryContact;
                    });

                    if (defaultAgencyEmployee) {
                        return defaultAgencyEmployee.email;
                    }

                    return 'AgencyHasNoContact';
                }
                case 'internal': {
                    const defaultInternal = emailInboxes.find((e) => {
                        return String(e.id) === String(eentityId);
                    });

                    if (defaultInternal) {
                        return defaultInternal.name;
                    }

                    return 'InternalNotFound';
                }
                case 'location': {
                    const defaultLocationEmployee = employees.find((e) => {
                        return String(e.locationId) === String(eentityId) && e.isPrimaryContact;
                    });

                    if (defaultLocationEmployee) {
                        return defaultLocationEmployee.email;
                    }

                    return 'LocationHasNoContact';
                }
                default:
                    return receiver;
            }
        };

        const agencyIds = (receiver) => {
            const [eentity, eentityId] = receiver.split('_', 2);
            switch (eentity) {
                case 'agency': {
                    return eentityId;
                }
                default:
                    return undefined;
            }
        };
        const preppedValues = {
            ...values,
            receiver: values.receiver.filter((receiver) => !!receiver).map(resolveEmailAddresses),
            cc: values.cc.filter((receiver) => !!receiver).map(resolveEmailAddresses),
            bcc: values.bcc.filter((receiver) => !!receiver).map(resolveEmailAddresses),
            attachments: values.attachments.map(
                (attachment) => attachment.path || attachment.payload
            ),
            use_default_signature: false,
            is_premium: isCurrentCustomerPremium,
            use_receiver_seperately: isSetUseReceiverSeperately,
            draft_id: draftData?.id || null,
            email_template_id: templateId,
        };
        const preppedExtraEntries = {
            ...extraEntities,
            agencies: values.receiver
                .filter((receiver) => !!receiver)
                .map(agencyIds)
                .filter((id) => !!id),
        };
        return api[EMAIL_RESOURCE]
            .sendEmail(preppedValues)
            .catch((error) => {
                // Unlike the bookkeeping .catch below, this one must not swallow
                // the error - the send itself failed (or we simply never saw the
                // response, e.g. a timeout), so the dialog needs to stay open and
                // the button needs to become clickable again. But since the mail
                // may have gone out anyway before the response was lost, tell the
                // user to check history instead of just "failed" - clicking
                // "Absenden" again on the assumption nothing happened is exactly
                // what causes duplicate sends.
                throw {
                    formError: true,
                    message:
                        'E-Mail konnte nicht bestätigt werden. Bitte im Verlauf prüfen, ob sie bereits versendet wurde, bevor erneut gesendet wird.',
                    cause: error,
                };
            })
            .then(() => {
                // The email has been sent successfully at this point. Everything below
                // is follow-up bookkeeping (marking the customer record, notifying the
                // caller via onSent, etc.). A failure in that bookkeeping must not be
                // reported to the user as a failed send - the mail already went out.
                const bookkeeping = Promise.resolve()
                    .then(() => {
                        if (entity === INFOMATERIAL_ENTITY && customerId) {
                            return dispatch(
                                updateCustomer({
                                    id: customerId,
                                    infomaterialSentAt: new Date(),
                                    infomaterialSentVia: ['email'],
                                })
                            );
                        }
                        return null;
                    })
                    .then(() =>
                        onSent
                            ? onSent({ mail: preppedValues, extraEntities: preppedExtraEntries })
                            : null
                    )
                    .catch((error) => {
                        console.error(
                            'SendMailForm: email was sent successfully, but post-send bookkeeping failed =>',
                            error
                        );
                        enqueueSnackbar('E-Mail versendet, aber Aktualisierung fehlgeschlagen', {
                            variant: 'warning',
                        });
                        return null;
                    });

                return bookkeeping;
            });
    };

    const handleUpdateEmailDraft = useCallback(
        async (draftId, fieldName, fieldValue) => {
            //console.log(`Updating draft ${draftId}: setting ${fieldName} to`, fieldValue);
            const date = new Date().toISOString();

            const data = {
                id: draftId,
                [fieldName]: normalizeDraftFieldValue(fieldName, fieldValue),
                owner_employee_id: currentUser.employeeId,
                customer_id: entityId || null,
                parent_id: null,
                updated_at: date,
                customerId: entityId || null,
                email_template_id: templateId,
            };

            saveDraft(data)
                .then((response) => {
                    //console.log('Draft updated on server:', response);
                    // check if response contains new draft id
                    if (response && response.id) {
                        //console.log('New draft id:', response.id);
                        setDraftData((prevDraftData) => ({
                            ...prevDraftData,
                            id: response.id,
                        }));
                    }
                    return true;
                })
                .catch((error) => {
                    console.error('Error updating draft on server:', error);
                });
            return true;
        },
        [currentUser.employeeId, entityId, normalizeDraftFieldValue, saveDraft, templateId]
    );

   const onDraftChange = useCallback(
        (name,value) => {
            //console.log('Draft change value =>',value, "name =>",name);
            let draft_field_name = "";
            switch(name){
                case 'receiver':
                    draft_field_name = 'to';
                    break;
                case 'cc':
                    draft_field_name = 'cc';
                    break;
                case 'bcc':
                    draft_field_name = 'bcc';
                    break;
                case 'from':
                    draft_field_name = 'from';
                    break;
                case 'subject':
                    draft_field_name = 'subject';
                    break;
                case 'text':
                    draft_field_name = 'body';
                    break;
                case 'attachments':
                    draft_field_name = 'attachments';
                    break;
                default:
                    console.log(`Unknown field name: ${name}`);
                    return;
            }
            //console.log('Draft changed field name =>',draft_field_name, "value =>",value);
            handleUpdateEmailDraft(draftData?.id, draft_field_name, value);
        },[draftData, handleUpdateEmailDraft]
    );

    return (
        <Form
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={sendMailSchema}
            enableReinitialize
        >
            <Grid container spacing={2} alignItems="flex-start">
                {/* LEFT SIDE */}
                <Grid container item xs={9} spacing={2}>
                {emailTemplate && (
                    <Grid item xs={12}>
                        <Alert severity="info">
                            Erstellt über das Email-Template: {emailTemplate.subject}                            
                        </Alert>
                    </Grid>
                )}
                    <Grid item xs={6}>
                        <Select
                            name="from"
                            label="Absender"
                            options={
                                emailTemplate &&
                                !emailTemplate.emailInboxDefaultEditable &&
                                !isCurrentCustomerPremium
                                    ? fromAddressOptionsNotEditable
                                    : fromAddressOptions
                            }
                            disabled={emailTemplate && !emailTemplate.emailInboxDefaultEditable}
                            fullWidth
                            onChange ={onDraftChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <MultiChipSelect
                            name="receiver"
                            label="Empfänger"
                            options={receiverOptions}
                            groupBy={(option) => option.groupTitle}
                            fullWidth
                            disableCloseOnSelect={true}
                            disabled={emailTemplate && !emailTemplate.receiverDefaultEditable}
                            creatable
                            onChange ={onDraftChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <MultiChipSelect
                            name="cc"
                            label="Kopie an"
                            options={receiverOptions}
                            groupBy={(option) => option.groupTitle}
                            fullWidth
                            disabled={emailTemplate && !emailTemplate.ccDefaultEditable}
                            creatable
                            onChange ={onDraftChange}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <MultiChipSelect
                            name="bcc"
                            label="Blindkopie an"
                            options={receiverOptions}
                            groupBy={(option) => option.groupTitle}
                            fullWidth
                            creatable
                            onChange ={onDraftChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Divider className={classes.dividerMargin} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextInput
                            name="subject"
                            label="Betreff"
                            fullWidth
                            onChange ={onDraftChange}
                            onKeyPress={(e) => {
                                e.key === 'Enter' && e.preventDefault();
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Editor 
                            name="text" 
                            showRefreshButton 
                            onRefresh={onRefresh} 
                            onChange ={onDraftChange}
                            />
                    </Grid>
                </Grid>
                {/* RIGHT SIDE */}
                {/* Attachments */}

                <FileTreeAttachments 
                    name="attachments" 
                    label="Anhänge" 
                    roots={roots} 
                    onChange ={onDraftChange} 
                />
                {/* BOTTOM */}
                <Grid item xs={12}>
                    <Divider className={classes.dividerMargin} />
                </Grid>
                <Grid item xs={12} className={classes.end}>
                    <SubmitButton container={submitRef} label="Absenden" startIcon={<SendIcon />} />
                </Grid>
            </Grid>
        </Form>
    );
};

SendMailForm.propTypes = {
    entity: PropTypes.string.isRequired,
    entityId: IdPropType,
    templateId: IdPropType,
    attachments: PropTypes.arrayOf(MailAttachmentPropType),
    prefill: PrefillMailPropType,
    extraEntities: PropTypes.shape({}),
    onSent: PropTypes.func,
    submitRef: RefPropType,
    storedDraft: PropTypes.shape({}),
    draft: PropTypes.shape({}),
    draftId: IdPropType,
    draftSaveRef: PropTypes.shape({ current: PropTypes.func }),
    onDeleteDraft: PropTypes.func,
    onCancel: PropTypes.func,
};

SendMailForm.defaultProps = {
    templateId: null,
    attachments: null,
    entityId: null,
    prefill: null,
    extraEntities: null,
    onSent: null,
    submitRef: null,
    storedDraft: null,
    draft: null,
    draftId: null,
    draftSaveRef: null,
    onDeleteDraft: null,
    onCancel: null,
};

export default SendMailForm;
